import type { EntityName, OutboxOp, PullResult, PushResult } from '@cadencia/contracts';

/**
 * Transporte de sincronizacion. La interfaz permite probar el motor sin red
 * (docs/TEST_STRATEGY.md §4) y cambiar de backend sin tocar el motor.
 */
export interface SyncTransport {
  push(ops: OutboxOp[]): Promise<PushResult>;
  pull(cursor: number, limit: number): Promise<PullResult>;
}

export interface SyncOutcome {
  pushed: number;
  pulled: number;
  rejected: number;
  conflicts: number;
  /**
   * Filas de una entidad que este cliente no conoce, saltadas en la bajada.
   * Se cuenta y no solo se registra: un salto silencioso es justo la familia de
   * fallos de R-17, y un test tiene que poder afirmarlo sin leer el log.
   */
  unknownEntities: number;
  /**
   * Operaciones que el servidor NO aplico porque tiene algo que gana.
   *
   * No es un error: el `on conflict … where` no se cumplio, asi que no escribio
   * y tampoco fallo. Lo dice el propio servidor desde la migracion 0026; hasta
   * entonces venian dentro de `applied` y el cliente lo deducia comparando la
   * `rev` enviada con la devuelta (REAUD-002).
   *
   * Se cuenta, y no solo se registra, por el mismo motivo que
   * `unknownEntities`: un descarte silencioso tiene que poder afirmarse desde
   * un test sin leer el log.
   */
  superseded: number;
  cursor: number;
  error: string | null;
}

/** Tabla local correspondiente a cada entidad del servidor. */
export const ENTITY_TO_TABLE: Record<EntityName, string> = {
  profiles: 'profiles',
  user_preferences: 'userPreferences',
  gyms: 'gyms',
  equipment: 'equipment',
  available_loads: 'availableLoads',
  training_blocks: 'trainingBlocks',
  training_weeks: 'trainingWeeks',
  training_sessions: 'trainingSessions',
  session_exercises: 'sessionExercises',
  set_prescriptions: 'setPrescriptions',
  warmup_prescriptions: 'warmupPrescriptions',
  readiness_checkins: 'readinessCheckins',
  workout_logs: 'workoutLogs',
  performed_sets: 'performedSets',
  rest_events: 'restEvents',
  load_recommendations: 'loadRecommendations',
  progression_state: 'progressionState',
  training_decisions: 'trainingDecisions',
  cardio_prescriptions: 'cardioPrescriptions',
  cardio_logs: 'cardioLogs',
  nutrition_targets: 'nutritionTargets',
  meal_plans: 'mealPlans',
  planned_meals: 'plannedMeals',
  meal_logs: 'mealLogs',
  daily_weights: 'dailyWeights',
  user_recipe_settings: 'userRecipeSettings',
  pantry_items: 'pantryItems',
  shopping_lists: 'shoppingLists',
  shopping_list_items: 'shoppingListItems',
  body_measurements: 'bodyMeasurements',
  weekly_checkins: 'weeklyCheckins',
  daily_activity: 'dailyActivity',
  evidence_searches: 'evidenceSearches',
  evidence_sources: 'evidenceSources',
  ai_requests: 'aiRequests',
  ai_decisions: 'aiDecisions',
  ai_usage_budget: 'aiUsageBudget',
};
